import { useState, type ReactNode } from "react";
import {
  AlertTriangle,
  ArrowRight,
  Blocks,
  BriefcaseBusiness,
  CheckCircle2,
  ClipboardCheck,
  Code2,
  Compass,
  Database,
  Gauge,
  GitPullRequest,
  Layers3,
  Network,
  Rocket,
  SearchCheck,
  ShieldCheck,
  Sparkles,
  Wrench,
  Workflow,
} from "lucide-react";
import PublicLayout from "@/components/public/PublicLayout";
import OfferInterestSignup from "@/components/public/OfferInterestSignup";
import OpportunityIntakeForm from "@/components/public/OpportunityIntakeForm";
import ProjectTranslator from "@/components/public/ProjectTranslator";
import ProofSection from "@/components/public/ProofSection";
import { TrackedAnchor, TrackedLink } from "@/components/public/TrackedCta";
import UpdatesSignup from "@/components/public/UpdatesSignup";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { trackPublicFunnelEvent } from "@/lib/publicFunnel";
import { entryPointConfigs } from "@/lib/opportunityPayload";
import type { ProjectIntakeHandoff } from "@/lib/projectTranslator";
import EntryPointPage from "./EntryPointPage";

const entryCards = [
  {
    title: "Build My SVM Operator",
    label: "Ongoing support",
    path: "/ai-operators",
    icon: Blocks,
    text: "See every open request, decision, and next step in one place, with SVM staying involved to keep it useful.",
  },
  {
    title: "Build a New App",
    label: "App build",
    path: "/app-build",
    icon: Code2,
    text: "Start with the business need. SVM shapes the first useful release, builds it, launches it, and stays involved after it goes live.",
  },
  {
    title: "Rescue or Finish My App",
    label: "App Rescue",
    path: "/app-rescue",
    icon: GitPullRequest,
    text: "Bring us a stalled repo, preview, MVP, or no-code build. We find the blocker and help get it ready for real people to use.",
  },
  {
    title: "Audit My Business Systems",
    label: "Find the gaps",
    path: "/systems-audit",
    icon: SearchCheck,
    text: "See where work gets lost, repeated, or delayed before you buy or build another tool.",
  },
  {
    title: "Evaluate a Domain, Venture, or Deal",
    label: "Make a clear decision",
    path: "/venture-evaluation",
    icon: Compass,
    text: "Decide whether an asset, partnership, domain, or deal is worth building, pursuing, selling, pausing, or passing on.",
  },
];

const operatorFlow = [
  {
    number: "01",
    label: "Bring it in",
    signal: "Leads and requests arrive through forms, email, referrals, and team conversations.",
    movement: "They are captured with the right context, priority, and owner.",
  },
  {
    number: "02",
    label: "Make the call",
    signal: "Approvals and decisions get buried in email, chat, or meetings.",
    movement: "The decision stays with the work, so everyone knows what was decided and why.",
  },
  {
    number: "03",
    label: "Keep it moving",
    signal: "Follow-up, files, tasks, and customer promises are easy to lose track of.",
    movement: "The next step is clear, overdue work stands out, and delivery keeps moving.",
  },
  {
    number: "04",
    label: "Learn from it",
    signal: "Useful details and results often disappear once a project ends.",
    movement: "SVM keeps the history, shows patterns, and helps the next job run better.",
  },
];

const appRescueSignals = [
  "Find what is actually broken across the product, data, sign-in, payments, or deployment.",
  "Choose the smallest version that is ready for real people to use.",
  "Fix, ship, and verify it so you know what works and what still needs attention.",
];

const appRescuePainPoints = [
  {
    Icon: AlertTriangle,
    title: "Demo anxiety",
    text: "The app looks close, but every walkthrough reveals another bug, missing path, or confusing moment.",
  },
  {
    Icon: Database,
    title: "Untrusted data",
    text: "Records disappear, duplicate, sync oddly, or fail in the exact workflow customers need to trust.",
  },
  {
    Icon: ShieldCheck,
    title: "Launch risk",
    text: "Sign-in, permissions, payments, emails, or deployment are not stable enough for real users yet.",
  },
];

const appRescueDeliverables = [
  {
    title: "Blocker map",
    text: "A plain-language readout of what is broken, what is risky, and what is only cosmetic.",
  },
  {
    title: "Critical-path fixes",
    text: "Focused repair work on the path that must work before a customer, team member, or buyer can use it.",
  },
  {
    title: "Release decision",
    text: "A concrete call: ship, pilot, hold, rebuild part of it, or retire the wrong branch of work.",
  },
];

const appRescueSprintSteps = [
  {
    number: "01",
    label: "Inspect",
    title: "Review the artifact",
    text: "SVM looks at the app, repo, preview, no-code build, or walkthrough and names the highest-risk unknowns.",
  },
  {
    number: "02",
    label: "Triage",
    title: "Separate blockers from wishes",
    text: "We identify what must work for real use and what can safely wait until after the first rescue pass.",
  },
  {
    number: "03",
    label: "Repair",
    title: "Fix the critical path",
    text: "The sprint focuses on stability, user flow, data, access, payments, deployment, and other release blockers.",
  },
  {
    number: "04",
    label: "Verify",
    title: "Leave with a launch call",
    text: "You get a practical status: what is ready, what remains, and the next move if SVM should keep running it with you.",
  },
];

const appRescueInspectionAreas = [
  "User flow and first-session clarity",
  "Auth, roles, and permissions",
  "Database shape and data integrity",
  "Payments, email, and external services",
  "Deployment, environments, and rollback path",
  "Security basics and private data handling",
];

const appRescueFitSignals = [
  "You already have a repo, preview, live URL, no-code build, or clickable walkthrough.",
  "People are waiting on the app, but you do not trust the current version enough to launch.",
  "The project has become a pile of bugs, unclear scope, and half-finished feature ideas.",
  "You need a calm technical operator who can tell you what matters before adding more work.",
];

const appRescueNotFitSignals = [
  "There is no existing artifact to inspect yet. Start with the App Build path instead.",
  "The goal is to clone a large product without choosing a focused first release.",
  "The project requires legal, medical, financial, or compliance advice before product work can start.",
];

const appBuildSignals = [
  "Turn the business problem into a clear first release before choosing features or technology.",
  "Build the product around real users, permissions, decisions, and the tools that need to stay.",
  "Launch it with a plan for security, maintenance, support, and the next useful improvement.",
];

const proofPatterns = [
  {
    icon: Network,
    title: "Understand how the work runs",
    text: "We learn where requests start, who makes the decisions, what tools people use, and where things get stuck.",
  },
  {
    icon: Workflow,
    title: "Make the next step clear",
    text: "We connect forms, inboxes, tools, approvals, and follow-up so the right person knows what to do next.",
  },
  {
    icon: Gauge,
    title: "Keep it working",
    text: "We observe, secure, maintain, and improve the system after launch. You are not left to manage it alone.",
  },
];

const homePaths = [
  {
    number: "01",
    label: "Build",
    title: "An AI Operator",
    path: "/ai-operators",
    text: "Give important requests, decisions, and follow-up a clear home.",
  },
  {
    number: "02",
    label: "Launch",
    title: "A new app",
    path: "/app-build",
    text: "Turn the business need into a focused first release.",
  },
  {
    number: "03",
    label: "Rescue",
    title: "A stuck product",
    path: "/app-rescue",
    text: "Find the blocker, fix the important parts, and get moving again.",
  },
  {
    number: "04",
    label: "Decide",
    title: "What is worth pursuing",
    path: "/venture-evaluation",
    text: "Evaluate an asset, deal, or idea before you spend more time on it.",
  },
];

const systemMoves = [
  {
    number: "01",
    label: "Clarify",
    text: "Name the work that matters and the constraint getting in its way.",
  },
  {
    number: "02",
    label: "Shape",
    text: "Choose the smallest useful system, workflow, or product to build first.",
  },
  {
    number: "03",
    label: "Ship",
    text: "Put it in front of real people, verify what works, and fix what does not.",
  },
  {
    number: "04",
    label: "Run",
    text: "Keep ownership, decisions, and the next improvement visible after launch.",
  },
];

export const PublicHome = () => (
  <PublicLayout>
    <SEO
      title="Shock Value Media - Build the Machine. Own the Outcome."
      description="Shock Value Media helps organizations turn scattered work and knowledge into usable AI capacity by deciding what to organize, automate, buy, or build."
      url="/"
    />
    <section className="relative isolate overflow-hidden bg-[#07090c] text-white">
      <div className="absolute inset-y-0 right-0 w-full lg:w-[62%]">
        <img
          src="/svm-operator-hero.jpg"
          alt="SVM Operator control surface showing work moving from intake through delivery"
          className="hidden h-full w-full object-cover object-[66%_center] opacity-75 md:block md:object-center"
          width="1440"
          height="810"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#07090c] via-[#07090c]/90 to-[#07090c]/10" />
        <div className="absolute inset-0 bg-[#07090c]/20" />
      </div>
      <div className="svm-ink-grid absolute inset-0 opacity-30" />
      <div className="container relative mx-auto px-4 pb-10 pt-20 sm:pt-24 lg:px-8 lg:pb-16 lg:pt-32">
        <div className="max-w-6xl">
          <div className="max-w-3xl animate-fade-in-up">
            <p className="mb-6 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.2em] text-amber-300">
              <span className="h-px w-10 bg-amber-300" />
              Shock Value Media / AI-capable systems
            </p>
            <h1 className="svm-display-tight mb-5 max-w-3xl text-[2.65rem] leading-[0.92] tracking-[-0.03em] sm:text-5xl md:mb-6 md:text-7xl lg:text-[5.35rem]">
              Turn scattered work into <span className="text-amber-300">a system your team—and AI—can run.</span>
            </h1>
            <p className="max-w-2xl text-base font-medium leading-7 text-slate-300 sm:text-lg md:text-xl">
              SVM helps you understand how work actually runs, organize the knowledge behind it, and decide what to automate, buy, or build.
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:mt-8 sm:flex-row">
              <TrackedLink
                to="/project-planner"
                tracking={{
                  label: "Get an AI Capacity Snapshot",
                  destination: "/project-planner",
                  placement: "home_hero_primary",
                  service: "project_translator",
                }}
              >
                <Button size="lg" className="w-full rounded-md bg-amber-300 px-6 font-bold text-slate-950 shadow-[0_0_40px_rgba(251,191,36,0.2)] hover:bg-amber-200 sm:w-auto">
                  Get an AI Capacity Snapshot
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </TrackedLink>
              <TrackedLink
                to="/app-rescue"
                tracking={{
                  label: "My app needs help",
                  destination: "/app-rescue",
                  placement: "home_hero_secondary",
                  service: "app_rescue",
                }}
              >
                <Button size="lg" variant="outline" className="w-full rounded-md border-white/30 bg-white/5 px-6 font-bold text-white backdrop-blur-sm hover:bg-white hover:text-slate-950 sm:w-auto">
                  My app needs help
                </Button>
              </TrackedLink>
            </div>
            <div className="mt-7 flex flex-wrap gap-x-4 gap-y-2 text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-slate-400 sm:gap-x-5 sm:text-xs">
              <span>Human-led direction</span>
              <span className="text-amber-300">/</span>
              <span>No technical brief required</span>
              <span className="text-amber-300">/</span>
              <span>Built for the work you actually do</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section aria-labelledby="snapshot-outcomes-heading" className="border-b border-slate-300 bg-white text-slate-950">
      <div className="container mx-auto grid gap-7 px-4 py-8 md:grid-cols-[0.72fr_1.28fr] md:items-end lg:px-8">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-rose-700">The free starting point</p>
          <h2 id="snapshot-outcomes-heading" className="mt-2 max-w-md text-3xl leading-tight md:text-4xl">Four questions. A practical first move.</h2>
        </div>
        <ol className="grid border-y border-slate-300 sm:grid-cols-3 sm:border-t sm:border-b-0">
          <li className="border-b border-slate-300 py-5 sm:border-b-0 sm:pr-5">
            <span className="font-mono text-xs font-bold text-rose-700">01</span>
            <h3 className="mt-3 text-lg font-bold">Your situation, plainly stated</h3>
            <p className="mt-2 text-sm font-medium leading-6 text-slate-600">Describe the work in your own words—no technical brief needed.</p>
          </li>
          <li className="border-b border-slate-300 py-5 sm:border-b-0 sm:border-l sm:px-5">
            <span className="font-mono text-xs font-bold text-rose-700">02</span>
            <h3 className="mt-3 text-lg font-bold">A likely first path</h3>
            <p className="mt-2 text-sm font-medium leading-6 text-slate-600">See whether the next move is an app, an operator, a rescue, or an audit.</p>
          </li>
          <li className="py-5 sm:border-l sm:border-slate-300 sm:pl-5">
            <span className="font-mono text-xs font-bold text-rose-700">03</span>
            <h3 className="mt-3 text-lg font-bold">A human-reviewed next step</h3>
            <p className="mt-2 text-sm font-medium leading-6 text-slate-600">Get a useful first phase before anyone asks you to choose a platform.</p>
          </li>
        </ol>
      </div>
    </section>

    <section aria-labelledby="home-paths-heading" className="svm-ink-grid bg-[#0d1118] text-white">
      <div className="container mx-auto px-4 py-10 lg:px-8 lg:py-12">
        <div className="mb-7 flex flex-col justify-between gap-3 border-b border-white/20 pb-5 sm:flex-row sm:items-end">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-amber-300">Already know the work?</p>
            <h2 id="home-paths-heading" className="mt-2 text-3xl md:text-4xl">Choose a direct path.</h2>
          </div>
          <p className="max-w-md text-sm font-medium leading-6 text-slate-400">Start where the pressure is most obvious. SVM will help shape the right first move from there.</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {homePaths.map((path) => (
              <TrackedLink
                key={path.path}
                to={path.path}
                tracking={{
                  label: path.title,
                  destination: path.path,
                  placement: "home_path_grid",
                }}
                className="group border border-white/15 bg-white/[0.03] p-5 transition-colors hover:border-amber-300 hover:bg-white/[0.06] lg:min-h-56"
              >
                <span className="mb-8 block font-mono text-xs text-amber-300">{path.number}</span>
                <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">{path.label}</span>
                <span className="flex items-center gap-2 text-lg font-bold text-white">
                  {path.title}
                  <ArrowRight className="h-4 w-4 text-amber-300 transition-transform group-hover:translate-x-1" />
                </span>
                <span className="mt-2 block max-w-[15rem] text-sm font-medium leading-6 text-slate-400">{path.text}</span>
              </TrackedLink>
            ))}
        </div>
      </div>
    </section>

    <section className="border-b border-slate-300 bg-[#f3eee4]">
      <div className="container mx-auto px-4 py-14 lg:px-8 lg:py-20">
        <div className="mb-10 grid gap-6 lg:grid-cols-[0.8fr_1.2fr] lg:items-end">
          <div>
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-rose-700">01 / The SVM way</p>
            <h2 className="max-w-xl text-4xl md:text-5xl">Make the messy middle visible.</h2>
          </div>
          <p className="max-w-2xl font-medium text-slate-700 lg:justify-self-end">
            Most important work does not fail because people do not care. It gets buried between the request, the decision, the handoff, and the next step. SVM helps make that path usable.
          </p>
        </div>
        <div className="grid border-y border-slate-300 md:grid-cols-2 lg:grid-cols-4">
          {systemMoves.map((move, index) => (
            <div key={move.number} className={`py-6 md:px-6 lg:py-7 ${index < systemMoves.length - 1 ? "border-b border-slate-300 lg:border-b-0 lg:border-r" : ""} ${index === 0 ? "md:pl-0" : ""}`}>
              <span className="mb-8 block font-mono text-xs font-bold text-rose-700">{move.number}</span>
              <h3 className="mb-3 text-2xl">{move.label}</h3>
              <p className="text-sm font-medium leading-6 text-slate-600">{move.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="border-b border-slate-300 bg-[#f3eee4]">
      <div className="container mx-auto grid px-4 lg:grid-cols-[0.82fr_1.18fr] lg:px-8">
        <div className="border-b border-slate-300 py-12 lg:border-b-0 lg:border-r lg:py-16 lg:pr-12">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-rose-700">02 / SVM Operator</p>
          <h2 className="text-4xl md:text-5xl">See what is coming in, what needs a decision, and what happens next.</h2>
          <p className="mt-5 max-w-xl font-medium text-slate-700">
            SVM Operator brings requests, leads, conversations, follow-up, and delivery into one place. Your team can see who owns the next step and where work is stuck.
          </p>
        </div>
        <div className="lg:pl-12">
          {operatorFlow.map((step) => (
            <div key={step.number} className="grid gap-3 border-b border-slate-300 py-7 last:border-b-0 md:grid-cols-[52px_190px_1fr] md:items-start">
              <span className="font-mono text-xs font-bold text-rose-700">{step.number}</span>
              <h3 className="text-lg font-bold">{step.label}</h3>
              <div className="grid gap-2 text-sm md:grid-cols-2">
                <p className="text-slate-500"><span className="mb-1 block text-[10px] font-bold uppercase text-slate-400">What happens now</span>{step.signal}</p>
                <p className="text-slate-800"><span className="mb-1 block text-[10px] font-bold uppercase text-emerald-700">What gets easier</span>{step.movement}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-[#0b0d10] py-16 text-white md:py-20">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="mb-10 grid gap-5 lg:grid-cols-[0.8fr_1.2fr] lg:items-end">
          <h2 className="max-w-2xl text-4xl md:text-5xl">We stay involved after launch.</h2>
          <p className="max-w-2xl font-medium text-slate-300 lg:justify-self-end">
            SVM keeps the system secure, useful, and improving. We watch how the work runs, maintain what we built, and make changes as your needs change.
          </p>
        </div>
        <div className="grid border-y border-white/15 lg:grid-cols-3">
          {proofPatterns.map((item, index) => {
            const Icon = item.icon;
            return (
              <div key={item.title} className={`py-7 lg:px-7 ${index < proofPatterns.length - 1 ? "border-b border-white/15 lg:border-b-0 lg:border-r" : ""} ${index === 0 ? "lg:pl-0" : ""}`}>
                <Icon className="mb-6 h-7 w-7 text-amber-300" />
                <h3 className="mb-3 text-2xl">{item.title}</h3>
                <p className="text-sm font-medium text-slate-400">{item.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    <section className="border-b border-slate-300 bg-white">
      <div className="container mx-auto grid gap-10 px-4 py-14 lg:grid-cols-[0.84fr_1.16fr] lg:px-8 lg:py-20">
        <div>
          <p className="mb-3 text-xs font-bold uppercase text-rose-700">App Builds</p>
          <h2 className="max-w-2xl text-4xl md:text-5xl">Need an app built around the way the work really happens?</h2>
          <p className="mt-5 max-w-xl font-medium text-slate-700">
            Bring the need, the people, and the result you want. SVM will help define the first useful version, build it, launch it, and keep improving it after real people start using it.
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <TrackedLink
              to="/app-build"
              tracking={{
                label: "Plan My App",
                destination: "/app-build",
                placement: "home_app_build_section",
                service: "app_build",
              }}
            >
              <Button size="lg" className="w-full rounded-md bg-slate-950 px-6 font-bold text-white hover:bg-rose-700 sm:w-auto">
                Plan My App
                <ArrowRight className="h-4 w-4" />
              </Button>
            </TrackedLink>
            <TrackedLink
              to="/project-planner"
              tracking={{
                label: "Help me choose the right path",
                destination: "/project-planner",
                placement: "home_app_build_section_secondary",
                service: "project_translator",
              }}
              className="inline-flex items-center justify-center text-sm font-bold text-slate-800 underline underline-offset-4"
            >
              Help me choose the right path
            </TrackedLink>
          </div>
        </div>
        <div className="border-t border-slate-300">
          {appBuildSignals.map((signal, index) => (
            <div key={signal} className="grid grid-cols-[42px_1fr] gap-3 border-b border-slate-300 py-5 text-sm font-semibold text-slate-700">
              <span className="font-mono text-xs text-rose-700">0{index + 1}</span>
              <span>{signal}</span>
            </div>
          ))}
          <p className="mt-5 text-sm font-medium text-slate-600">
            SVM can build customer portals, staff tools, workflow apps, and focused products. We will not force a large platform when a smaller first release can prove the value.
          </p>
        </div>
      </div>
    </section>

    <section className="border-b border-slate-300 bg-[#f3eee4]">
      <div className="container mx-auto grid lg:grid-cols-[0.9fr_1.1fr]">
        <div className="px-4 py-14 lg:px-8 lg:py-20">
          <p className="mb-3 text-xs font-bold uppercase text-rose-700">App Rescue</p>
          <h2 className="max-w-2xl text-4xl md:text-5xl">Have an app that is stuck?</h2>
          <p className="mt-5 max-w-xl font-medium text-slate-700">
            Bring us the repo, preview, MVP, or no-code build. We find what is blocking it, fix the most important problems, and get it ready for people to use, test, or buy. If it needs ongoing support, SVM can keep running it with you.
          </p>
          <div className="mt-8 border-y border-slate-300">
            {appRescueSignals.map((signal, index) => (
              <div key={signal} className="grid grid-cols-[34px_1fr] gap-3 border-b border-slate-300 py-4 text-sm font-semibold text-slate-700 last:border-b-0">
                <span className="font-mono text-xs text-rose-700">0{index + 1}</span>
                <span>{signal}</span>
              </div>
            ))}
          </div>
          <TrackedLink
            to="/app-rescue"
            tracking={{
              label: "My App Needs Help",
              destination: "/app-rescue",
              placement: "home_app_rescue_section",
              service: "app_rescue",
            }}
            className="mt-8 inline-flex"
          >
            <Button size="lg" className="rounded-md bg-slate-950 px-6 font-bold text-white hover:bg-rose-700">
              My App Needs Help
              <ArrowRight className="h-4 w-4" />
            </Button>
          </TrackedLink>
        </div>
        <div className="relative min-h-[360px] border-t border-slate-300 lg:min-h-full lg:border-l lg:border-t-0">
          <img
            src="/svm-app-rescue-hero.jpg"
            alt="Application diagnostic workspace showing issues and release readiness"
            className="absolute inset-0 h-full w-full object-cover"
            width="1672"
            height="941"
            loading="lazy"
          />
        </div>
      </div>
    </section>

    <section id="ways-to-begin" className="bg-white py-16 md:py-20">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="mb-10 max-w-3xl">
          <p className="mb-3 text-xs font-bold uppercase text-rose-700">Ways to begin</p>
          <h2 className="text-4xl md:text-5xl">Start with the problem you have now.</h2>
          <p className="mt-4 font-medium text-slate-600">Talk with SVM about how your operation runs, start a new app, rescue a stalled one, check your systems, or work through a business decision.</p>
        </div>
        <div className="border-t border-slate-300">
          {entryCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <TrackedLink
                key={card.path}
                to={card.path}
                tracking={{
                  label: card.title,
                  destination: card.path,
                  placement: "home_ways_to_begin",
                }}
                className="group grid gap-4 border-b border-slate-300 py-6 transition-colors hover:bg-[#f7f3eb] md:grid-cols-[56px_260px_1fr_32px] md:items-center md:px-3"
              >
                <span className={`flex h-11 w-11 items-center justify-center rounded-md ${index === 0 ? "bg-amber-300 text-slate-950" : "bg-slate-950 text-white"}`}><Icon className="h-5 w-5" /></span>
                <div>
                  <span className="mb-1 block text-[10px] font-bold uppercase text-rose-700">{card.label}</span>
                  <h3 className="text-xl">{card.title}</h3>
                </div>
                <p className="max-w-2xl text-sm font-medium text-slate-600">{card.text}</p>
                <ArrowRight className="h-5 w-5 text-slate-500 transition-transform group-hover:translate-x-1 group-hover:text-rose-700" />
              </TrackedLink>
            );
          })}
        </div>
      </div>
    </section>

    <ProofSection />

    <UpdatesSignup placement="home_post_proof" />

    <section className="border-y border-slate-300 bg-amber-300 py-14 text-slate-950 md:py-16">
      <div className="container mx-auto grid gap-8 px-4 lg:grid-cols-[1fr_auto] lg:items-center lg:px-8">
        <div>
          <p className="mb-3 text-xs font-bold uppercase text-rose-800">Stop running important work from memory</p>
          <h2 className="max-w-4xl text-4xl md:text-5xl">Give your team a clear way to see the work and keep it moving.</h2>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
          <TrackedLink
            to="/ai-operators"
            tracking={{
              label: "Talk Through Your Operation",
              destination: "/ai-operators",
              placement: "home_bottom_cta_primary",
              service: "ai_operator",
            }}
          >
            <Button size="lg" className="w-full rounded-md bg-slate-950 px-6 font-bold text-white hover:bg-rose-700 lg:w-full">
              Talk Through Your Operation
              <ArrowRight className="h-4 w-4" />
            </Button>
          </TrackedLink>
          <TrackedLink
            to="/app-rescue"
            tracking={{
              label: "My App Needs Help",
              destination: "/app-rescue",
              placement: "home_bottom_cta_secondary",
              service: "app_rescue",
            }}
            className="text-center text-sm font-bold underline underline-offset-4"
          >
            My App Needs Help
          </TrackedLink>
        </div>
      </div>
    </section>
  </PublicLayout>
);

export const AIBoardroomPage = () => (
  <PublicLayout>
    <SEO
      title="AI Boardroom Private Beta - Shock Value Media"
      description="Join the private beta for SVM's guided AI Boardroom, a human-reviewed strategy workspace for clearer business decisions."
      url="/ai-boardroom"
    />

    <section className="svm-ink-grid relative overflow-hidden bg-[#080b0f] py-14 text-white md:py-20">
      <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-rose-950/30 to-transparent" />
      <div className="container relative mx-auto grid gap-10 px-4 lg:grid-cols-[1.08fr_0.92fr] lg:items-center lg:px-8">
        <div className="max-w-4xl">
          <p className="mb-5 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.2em] text-amber-300">
            <span className="h-px w-10 bg-amber-300" />
            Shock Value Media / AI Boardroom private beta
          </p>
          <h1 className="svm-display-tight max-w-4xl text-5xl leading-[0.9] tracking-[-0.03em] md:text-7xl">
            A clearer room for the decision in front of you.
          </h1>
          <p className="mt-6 max-w-3xl text-lg font-medium leading-8 text-slate-300 md:text-xl">
            The AI Boardroom is a guided strategy workspace SVM is preparing for teams that need to think through a consequential next move with their real context in view.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <TrackedAnchor
              href="#boardroom-private-beta"
              tracking={{
                label: "Request AI Boardroom early access",
                destination: "/ai-boardroom#boardroom-private-beta",
                placement: "boardroom_beta_hero_primary",
                service: "general_inquiry",
              }}
              className="sm:inline-flex"
            >
              <Button size="lg" className="w-full rounded-md bg-amber-300 px-6 font-bold text-slate-950 shadow-[0_0_40px_rgba(251,191,36,0.2)] hover:bg-amber-200 sm:w-auto">
                Request early access
                <ArrowRight className="h-4 w-4" />
              </Button>
            </TrackedAnchor>
            <TrackedLink
              to="/ai-operators"
              tracking={{
                label: "See SVM Operator",
                destination: "/ai-operators",
                placement: "boardroom_beta_hero_secondary",
                service: "ai_operator",
              }}
              className="sm:inline-flex"
            >
              <Button size="lg" variant="outline" className="w-full rounded-md border-white/30 bg-white/5 px-6 font-bold text-white backdrop-blur hover:bg-white hover:text-slate-950 sm:w-auto">
                See SVM Operator
              </Button>
            </TrackedLink>
          </div>
          <p className="mt-5 text-sm font-medium text-slate-400">
            Private beta / human-reviewed / no credentials required
          </p>
        </div>

        <aside className="border-y border-white/15 py-6 lg:border-l lg:border-y-0 lg:py-2 lg:pl-10" aria-label="What the private beta is designed to clarify">
          <p className="mb-5 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">The room is designed to clarify</p>
          <div className="grid gap-0">
            {[
              "What decision matters most right now.",
              "Which constraints, tradeoffs, and unknowns are real.",
              "What the next bounded move should be.",
            ].map((outcome, index) => (
              <div key={outcome} className="grid grid-cols-[34px_1fr] gap-3 border-t border-white/15 py-4 first:border-t-0 first:pt-0">
                <span className="font-mono text-xs font-bold text-amber-300">0{index + 1}</span>
                <p className="text-sm font-semibold leading-6 text-slate-200">{outcome}</p>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </section>

    <section className="border-b border-slate-300 bg-white py-14 md:py-20">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="mb-10 max-w-3xl">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-rose-700">How the beta is shaped</p>
          <h2 className="svm-display-tight text-4xl md:text-5xl">Less generic advice. More useful context.</h2>
          <p className="mt-4 font-medium leading-7 text-slate-600">
            Early sessions are intentionally bounded. The goal is not to replace judgment or create another endless chat; it is to help a team frame the work, see the tradeoffs, and leave with a next action worth testing.
          </p>
        </div>
        <div className="grid border-y border-slate-300 md:grid-cols-3">
          {[
            ["01", "Frame the decision", "Start with the business situation, the pressure, and the choice that cannot stay vague."],
            ["02", "Surface the constraints", "Separate facts, assumptions, dependencies, and the places where a person must stay in control."],
            ["03", "Choose the next move", "Turn the discussion into a bounded experiment, review, or build path that can be verified."],
          ].map(([number, title, text], index) => (
            <div key={title} className={`py-6 md:px-6 md:py-8 ${index < 2 ? "border-b border-slate-300 md:border-b-0 md:border-r" : ""} ${index === 0 ? "md:pl-0" : ""}`}>
              <span className="mb-8 block font-mono text-xs font-bold text-rose-700">{number}</span>
              <h3 className="text-2xl font-bold">{title}</h3>
              <p className="mt-3 text-sm font-medium leading-6 text-slate-600">{text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="border-b border-slate-300 bg-[#f3eee4] py-14 md:py-20">
      <div className="container mx-auto grid gap-8 px-4 lg:grid-cols-[1fr_0.9fr] lg:items-start lg:px-8">
        <div>
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-rose-700">A deliberate opening</p>
          <h2 className="svm-display-tight text-4xl md:text-5xl">The public page is open. Live sessions are still by invitation.</h2>
          <p className="mt-5 max-w-2xl font-medium leading-7 text-slate-700">
            SVM is using the private beta to learn which decisions benefit from this format before opening broader access. Early-access requests go to the same private Operator workflow used for the rest of the public site.
          </p>
        </div>
        <div className="border-l-4 border-amber-300 bg-white p-6 md:p-8">
          <div className="flex items-start gap-3">
            <ShieldCheck className="mt-1 h-5 w-5 shrink-0 text-emerald-700" />
            <div>
              <h3 className="text-xl font-bold">What this does not ask for</h3>
              <p className="mt-3 text-sm font-medium leading-6 text-slate-600">
                No password, private source-system record, or production access. Share the kind of decision you want to think through, and SVM will follow up if the beta is a fit.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <UpdatesSignup
      placement="ai_boardroom_private_beta"
      defaultTopic="boardroom"
      title="Get AI Boardroom beta updates."
      description="Leave your email for early-access notes and an invitation when SVM opens the next small group of guided sessions."
    />
  </PublicLayout>
);

export const AiOperatorsPage = () => (
  <EntryPointPage
    config={entryPointConfigs.aiOperator}
    outcomes={[
      "Which requests, decisions, and follow-up should be easier to see and manage.",
      "What the system can handle and where a person should stay in control.",
      "The first workflow to set up so your team gets value without changing everything at once.",
    ]}
    proofPoints={[
      "Important work moves between people, inboxes, spreadsheets, or other tools.",
      "Your team spends too much time asking what happened or who owns the next step.",
      "You want help with routine coordination while keeping important decisions with people.",
    ]}
    examples={[
      "New requests arrive from several places, and the right person needs to see and follow up on each one.",
      "Client updates, tasks, files, and approvals need one clear owner and next step.",
      "Leaders need a useful weekly view of what is moving, stuck, late, or waiting for a decision.",
    ]}
    notFit={[
      "A chatbot with no real work, owner, or follow-up behind it.",
      "Replacing people before anyone understands how the work should run.",
      "A one-time prompt or document that does not need ongoing support.",
    ]}
    triagePrompts={[
      "The one workflow that causes the most confusion or missed work.",
      "The people and tools involved today.",
      "The result that would make the first setup useful.",
    ]}
  />
);

export const SystemsAuditPage = () => (
  <EntryPointPage
    config={entryPointConfigs.systemsAudit}
    outcomes={[
      "Where work gets repeated, delayed, dropped, or harder than it should be.",
      "Which problem is worth fixing first and which can wait.",
      "Whether the first answer is a process change, automation, an app, or SVM Operator.",
    ]}
    proofPoints={[
      "Your team repeats the same work every week.",
      "Important information is spread across inboxes, documents, spreadsheets, and tools.",
      "You know something should change but do not want to buy or build the wrong thing.",
    ]}
    examples={[
      "New opportunities arrive through email, text, forms, and referrals, but follow-up is inconsistent.",
      "A team wants automation but needs to know which repeated task is worth fixing first.",
      "Work is split between a CRM, inbox, calendar, spreadsheets, and delivery tools with no clear view of the whole process.",
    ]}
    notFit={[
      "A broad strategy report with no real workflow to examine.",
      "A request to justify a tool that has already been chosen.",
      "Bookkeeping, tax, legal, or formal compliance advice.",
    ]}
    triagePrompts={[
      "The work that wastes the most time or trust.",
      "Where information lives now.",
      "A recent example of the problem happening.",
    ]}
  />
);

export const AppBuildPage = () => (
  <EntryPointPage
    config={entryPointConfigs.appBuild}
    outcomes={[
      "Who the app is for and the job it needs to make easier.",
      "What belongs in the first useful release and what should wait.",
      "A build, launch, and support path based on the real work instead of a loose feature list.",
    ]}
    proofPoints={[
      "You know the business problem or customer need, even if you do not know the technology.",
      "A portal, staff tool, workflow app, or focused product would make the work easier.",
      "You want someone to help shape, build, launch, and maintain the product.",
    ]}
    examples={[
      "A customer portal where people can submit requests, see status, and receive updates.",
      "A staff tool that replaces spreadsheets and repeated handoffs with one clear workflow.",
      "A focused first product for an idea that needs real users and feedback before a larger build.",
    ]}
    notFit={[
      "A request to copy a large product without choosing a focused first release.",
      "A feature list with no clear user, job, or business reason behind it.",
      "A disposable build with no plan for security, launch, maintenance, or support.",
    ]}
    triagePrompts={[
      "Who will use the app and what they need to accomplish.",
      "How the work is handled today.",
      "The one result the first release must deliver.",
    ]}
  />
);

export const AppRescuePage = () => (
  <PublicLayout>
    <SEO
      title="App Rescue Sprint - Shock Value Media"
      description="A focused rescue path for stuck apps, previews, MVPs, repos, and no-code builds that need a trustworthy launch plan."
      url="/app-rescue"
    />

    <section className="relative isolate overflow-hidden bg-[#07090c] text-white">
      <div className="absolute inset-0">
        <img
          src="/svm-app-rescue-hero.jpg"
          alt="Application diagnostic workspace showing issues and release readiness"
          className="hidden h-full w-full object-cover object-[68%_center] opacity-50 md:block"
          width="1672"
          height="941"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#07090c] via-[#07090c]/92 to-[#07090c]/45" />
      </div>
      <div className="svm-ink-grid absolute inset-0 opacity-35" />
      <div className="container relative mx-auto grid gap-10 px-4 py-12 md:py-14 lg:min-h-[calc(100vh-72px)] lg:grid-cols-[minmax(0,1fr)_430px] lg:items-center lg:px-8 lg:py-20">
        <div className="max-w-4xl">
          <p className="mb-5 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.2em] text-amber-300">
            <span className="h-px w-10 bg-amber-300" />
            Shock Value Media / App Rescue Sprint
          </p>
          <h1 className="svm-display-tight max-w-4xl text-4xl leading-[0.94] tracking-[-0.03em] md:text-6xl lg:text-[5.2rem] lg:leading-[0.9]">
            Turn a stuck app into <span className="text-amber-300">a launch path you can trust.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg font-medium leading-8 text-slate-300 md:text-xl">
            Bring the repo, preview, MVP, or no-code build. SVM finds the real blocker, fixes the critical path, and tells you whether to ship, pilot, rebuild, or stop spending in the wrong direction.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <TrackedAnchor
              href="#opportunity-intake"
              tracking={{
                label: "Start an App Rescue Review",
                destination: "/app-rescue#opportunity-intake",
                placement: "app_rescue_hero_primary",
                service: "app_rescue",
              }}
              className="sm:inline-flex"
            >
              <Button size="lg" className="w-full rounded-md bg-amber-300 px-6 font-bold text-slate-950 shadow-[0_0_44px_rgba(251,191,36,0.22)] hover:bg-amber-200 sm:w-auto">
                Start an App Rescue Review
                <ArrowRight className="h-4 w-4" />
              </Button>
            </TrackedAnchor>
            <TrackedLink
              to="/project-planner"
              tracking={{
                label: "Help me decide first",
                destination: "/project-planner",
                placement: "app_rescue_hero_secondary",
                service: "project_translator",
              }}
            >
              <Button size="lg" variant="outline" className="w-full rounded-md border-white/30 bg-white/5 px-6 font-bold text-white backdrop-blur-sm hover:bg-white hover:text-slate-950 sm:w-auto">
                Help me decide first
              </Button>
            </TrackedLink>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2 text-xs font-semibold uppercase tracking-[0.14em] text-slate-400">
            <span>No passwords in the form</span>
            <span className="text-amber-300">/</span>
            <span>Human reviewed</span>
            <span className="text-amber-300">/</span>
            <span>Diagnosis before more features</span>
          </div>
        </div>

        <aside className="hidden rounded-md border border-white/15 bg-white/10 p-6 shadow-[0_30px_90px_rgba(0,0,0,0.38)] backdrop-blur lg:block" aria-label="App rescue diagnostic snapshot">
          <div className="mb-5 flex items-center justify-between gap-4 border-b border-white/15 pb-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-amber-300">Rescue focus</p>
              <h2 className="mt-2 text-2xl font-black">Get to the launch decision.</h2>
            </div>
            <Sparkles className="h-7 w-7 text-amber-300" />
          </div>
          <div className="grid gap-3">
            {appRescueDeliverables.map((item, index) => (
              <div key={item.title} className="grid grid-cols-[34px_1fr] gap-3 rounded-md border border-white/10 bg-[#07090c]/55 p-4">
                <span className="font-mono text-xs font-bold text-amber-300">0{index + 1}</span>
                <div>
                  <h3 className="font-bold">{item.title}</h3>
                  <p className="mt-1 text-sm font-medium leading-6 text-slate-300">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </section>

    <section className="border-b border-slate-300 bg-[#f3eee4] py-14 md:py-16">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="mb-10 grid gap-6 lg:grid-cols-[0.78fr_1.22fr] lg:items-end">
          <div>
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-rose-700">When App Rescue matters</p>
            <h2 className="svm-display-tight text-4xl md:text-5xl">The app is close enough to hurt.</h2>
          </div>
          <p className="max-w-2xl font-medium leading-7 text-slate-700 lg:justify-self-end">
            Stuck apps waste attention because they keep looking almost done. SVM’s job is to stop the drift: inspect the artifact, name the real release blockers, and make the next move obvious.
          </p>
        </div>

        <div className="grid border-y border-slate-300 lg:grid-cols-3">
          {appRescuePainPoints.map((item, index) => {
            const Icon = item.Icon;
            return (
              <article key={item.title} className={`py-7 lg:px-7 ${index < appRescuePainPoints.length - 1 ? "border-b border-slate-300 lg:border-b-0 lg:border-r" : ""} ${index === 0 ? "lg:pl-0" : ""}`}>
                <Icon className="mb-6 h-7 w-7 text-rose-700" />
                <h3 className="mb-3 text-2xl font-black">{item.title}</h3>
                <p className="text-sm font-semibold leading-6 text-slate-700">{item.text}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>

    <section className="border-b border-slate-300 bg-white py-14 md:py-20">
      <div className="container mx-auto grid gap-10 px-4 lg:grid-cols-[0.86fr_1.14fr] lg:px-8">
        <div>
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-rose-700">The rescue offer</p>
          <h2 className="svm-display-tight text-4xl md:text-5xl">A bounded sprint before an open-ended rebuild.</h2>
          <p className="mt-5 max-w-xl font-medium leading-7 text-slate-700">
            App Rescue is for the moment when “just add this feature” has stopped helping. SVM starts with diagnosis and release judgment, then repairs the smallest path that changes whether the app can be used.
          </p>
          <TrackedAnchor
            href="#opportunity-intake"
            tracking={{
              label: "Share the stuck app",
              destination: "/app-rescue#opportunity-intake",
              placement: "app_rescue_offer_section",
              service: "app_rescue",
            }}
            className="mt-7 inline-flex items-center gap-2 text-sm font-bold text-slate-950 underline decoration-amber-400 decoration-2 underline-offset-4 hover:text-rose-700"
          >
            Share the stuck app
            <ArrowRight className="h-4 w-4" />
          </TrackedAnchor>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          {appRescueDeliverables.map((item, index) => (
            <article key={item.title} className="rounded-md border border-slate-300 bg-[#fffdf6] p-5 shadow-sm">
              <span className="mb-8 block font-mono text-xs font-bold text-rose-700">0{index + 1}</span>
              <h3 className="text-2xl font-black">{item.title}</h3>
              <p className="mt-3 text-sm font-semibold leading-6 text-slate-700">{item.text}</p>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section className="border-b border-slate-300 bg-[#0b0d10] py-14 text-white md:py-20">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="mb-10 grid gap-5 lg:grid-cols-[0.78fr_1.22fr] lg:items-end">
          <div>
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-amber-300">How the sprint runs</p>
            <h2 className="svm-display-tight text-4xl md:text-5xl">Four moves from confusion to release judgment.</h2>
          </div>
          <p className="max-w-2xl font-medium leading-7 text-slate-300 lg:justify-self-end">
            Access, scope, and risk determine the exact plan. The operating principle stays the same: no more feature sprawl until the launch blockers are visible.
          </p>
        </div>

        <div className="grid border-y border-white/15 md:grid-cols-2 xl:grid-cols-4">
          {appRescueSprintSteps.map((step, index) => (
            <article key={step.number} className={`py-7 md:px-6 ${index < appRescueSprintSteps.length - 1 ? "border-b border-white/15 xl:border-b-0 xl:border-r" : ""} ${index === 0 ? "md:pl-0" : ""}`}>
              <span className="mb-8 block font-mono text-xs font-bold text-amber-300">{step.number}</span>
              <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">{step.label}</p>
              <h3 className="mb-3 text-2xl font-black">{step.title}</h3>
              <p className="text-sm font-medium leading-6 text-slate-300">{step.text}</p>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section className="border-b border-slate-300 bg-white py-14 md:py-20">
      <div className="container mx-auto grid gap-10 px-4 lg:grid-cols-[1fr_1fr] lg:px-8">
        <div>
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-rose-700">What SVM inspects</p>
          <h2 className="svm-display-tight text-4xl md:text-5xl">The parts that usually decide whether an app is safe to use.</h2>
          <p className="mt-5 max-w-xl font-medium leading-7 text-slate-700">
            Most rescues are not one bug. They are a chain: the user cannot finish the job, the data cannot be trusted, or the deployment cannot be repeated. The review looks across the whole path.
          </p>
        </div>

        <div className="rounded-md border border-slate-300 bg-[#f7f2e8] p-5 md:p-7">
          <div className="mb-5 flex items-center gap-3">
            <ClipboardCheck className="h-6 w-6 text-emerald-700" />
            <h3 className="text-2xl font-black">Launch-readiness checklist</h3>
          </div>
          <div className="grid gap-3">
            {appRescueInspectionAreas.map((area) => (
              <div key={area} className="flex items-start gap-3 border-t border-slate-300 pt-3 first:border-t-0 first:pt-0">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
                <p className="text-sm font-bold leading-6 text-slate-700">{area}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="border-b border-slate-300 bg-[#f3eee4] py-14 md:py-20">
      <div className="container mx-auto grid gap-10 px-4 lg:grid-cols-[1.02fr_0.98fr] lg:px-8">
        <div>
          <div className="mb-5 flex items-center gap-3 text-emerald-700">
            <Rocket className="h-6 w-6" />
            <h2 className="svm-display-tight text-4xl text-slate-950 md:text-5xl">Start here if...</h2>
          </div>
          <div className="border-t border-slate-300">
            {appRescueFitSignals.map((item) => (
              <div key={item} className="flex gap-3 border-b border-slate-300 py-4">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
                <p className="font-semibold leading-7 text-slate-700">{item}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-md border border-slate-300 bg-white p-5 md:p-7">
          <div className="mb-5 flex items-center gap-3 text-slate-500">
            <Wrench className="h-6 w-6" />
            <h2 className="svm-display-tight text-3xl text-slate-950 md:text-4xl">Use another path if...</h2>
          </div>
          <div className="border-t border-slate-300">
            {appRescueNotFitSignals.map((item) => (
              <div key={item} className="border-b border-slate-300 py-4 text-sm font-semibold leading-6 text-slate-600 last:border-b-0">
                {item}
              </div>
            ))}
          </div>
          <TrackedLink
            to="/app-build"
            tracking={{
              label: "Start an app build instead",
              destination: "/app-build",
              placement: "app_rescue_not_fit",
              service: "app_build",
            }}
            className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-slate-950 underline decoration-amber-400 decoration-2 underline-offset-4 hover:text-rose-700"
          >
            Start an app build instead
            <ArrowRight className="h-4 w-4" />
          </TrackedLink>
        </div>
      </div>
    </section>

    <section className="border-b border-slate-300 bg-amber-300 py-10 text-slate-950">
      <div className="container mx-auto grid gap-6 px-4 md:grid-cols-[1fr_auto] md:items-center lg:px-8">
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-rose-800">If the app exists but you do not trust it</p>
          <h2 className="svm-display-tight max-w-4xl text-4xl md:text-5xl">Bring the evidence. SVM will help you make the next call.</h2>
        </div>
        <TrackedAnchor
          href="#opportunity-intake"
          tracking={{
            label: "Request the rescue review",
            destination: "/app-rescue#opportunity-intake",
            placement: "app_rescue_pre_intake_band",
            service: "app_rescue",
          }}
        >
          <Button size="lg" className="w-full rounded-md bg-slate-950 px-6 font-bold text-white hover:bg-rose-700 md:w-auto">
            Request the rescue review
            <ArrowRight className="h-4 w-4" />
          </Button>
        </TrackedAnchor>
      </div>
    </section>

    <div>
      <OpportunityIntakeForm config={entryPointConfigs.appRescue} />
    </div>

    <OfferInterestSignup config={entryPointConfigs.appRescue} />
  </PublicLayout>
);

export const VentureEvaluationPage = () => (
  <EntryPointPage
    config={entryPointConfigs.ventureEvaluation}
    outcomes={[
      "The decision you are actually trying to make.",
      "The upside, limits, unknowns, and next commitment behind each option.",
      "A clear recommendation to move, pause, partner, sell, or pass.",
    ]}
    proofPoints={[
      "There is a real asset, audience, partner, buyer, or opportunity to consider.",
      "The choice affects where you spend time, money, or build capacity.",
      "You need a business decision, not only a score or valuation.",
    ]}
    examples={[
      "A domain, app, audience, or partnership that may deserve a build, sale, partnership, pause, or pass.",
      "A dormant asset that might become a product, service, or acquisition opportunity.",
      "A deal that looks interesting but needs a practical view of the work and commitment involved.",
    ]}
    notFit={[
      "Bulk domain appraisal or day-to-day portfolio management.",
      "Legal, securities, tax, or investment advice.",
      "An idea with no asset, audience, partner, or real decision yet.",
    ]}
    triagePrompts={[
      "What the asset or opportunity is and who is involved.",
      "The decision and options in front of you.",
      "What makes the decision matter now.",
    ]}
  />
);

export const ProjectPlannerPage = () => {
  const [handoff, setHandoff] = useState<ProjectIntakeHandoff | null>(null);

  return (
    <PublicLayout>
      <SEO
        title="AI Capacity Snapshot - Shock Value Media"
        description="Describe how work runs in plain language and get a guided first plan for what to organize, automate, buy, or build."
        url="/project-planner"
      />
      <section className="svm-ink-grid bg-[#080b0f] py-8 text-white md:py-12">
        <div className="container mx-auto grid gap-6 px-4 md:grid-cols-[1fr_auto] md:items-end lg:px-8">
          <div>
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-amber-300">SVM AI Capacity Snapshot</p>
            <h1 className="svm-display-tight max-w-4xl text-4xl leading-[0.94] md:text-5xl">See your next capacity move.</h1>
            <p className="mt-4 max-w-2xl font-medium leading-7 text-slate-300">
              Describe how work runs in ordinary language. Four short questions turn it into a practical first draft.
            </p>
          </div>
          <div className="flex gap-5 border-t border-white/15 pt-4 text-xs font-bold uppercase tracking-[0.14em] text-slate-300 md:border-l md:border-t-0 md:pl-6 md:pt-0">
            <span>4 questions</span>
            <span className="text-emerald-300">Human reviewed</span>
          </div>
        </div>
      </section>

      <ProjectTranslator onHandoff={setHandoff} onRestart={() => setHandoff(null)} />

      {handoff && (
        <div id="project-intake" className="scroll-mt-20">
          <OpportunityIntakeForm
            key={`${handoff.configKey}-${handoff.initialValues.projectPlanRecommendation}`}
            config={entryPointConfigs[handoff.configKey]}
            initialValues={handoff.initialValues}
          />
        </div>
      )}
    </PublicLayout>
  );
};

export const WorkPage = () => (
  <PublicLayout>
    <SEO
      title="Work - Shock Value Media"
      description="Ways SVM helps organizations build apps, rescue stalled products, organize ongoing work, and make clearer technology decisions."
      url="/work"
    />
    <section className="bg-slate-950 py-16 text-white md:py-24">
      <div className="container mx-auto px-4 lg:px-8">
        <p className="mb-4 text-sm font-semibold uppercase tracking-normal text-emerald-300">How SVM helps</p>
        <h1 className="mb-6 max-w-4xl text-4xl font-bold md:text-6xl">Take important work from unclear or stuck to useful and running.</h1>
        <p className="max-w-3xl text-lg text-slate-300">
          SVM helps define the right first move, builds what is needed, and stays involved so the result keeps working after launch.
        </p>
      </div>
    </section>
    <section className="py-16">
      <div className="container mx-auto grid gap-4 px-4 lg:grid-cols-2 lg:px-8">
        {[
          ["SVM Operator", "Keep incoming requests, decisions, owners, follow-up, and delivery clear across ongoing work."],
          ["New app builds", "Turn a business need into a focused first release, then build, launch, and support it."],
          ["App Rescue", "Find what is blocking an existing app and get the smallest trustworthy version ready for use."],
          ["Systems audits", "See where work gets lost or repeated and choose the first process worth fixing."],
          ["Venture decisions", "Review an asset, partnership, or deal and decide whether to move, pause, partner, or pass."],
        ].map(([title, text]) => (
          <div key={title} className="rounded-md border border-slate-200 p-6">
            <h2 className="mb-3 text-2xl font-bold">{title}</h2>
            <p className="text-slate-600">{text}</p>
          </div>
        ))}
      </div>
    </section>
    <ProofSection heading="Selected work" intro="Published examples from SVM engagements." />
    <UpdatesSignup
      placement="work_selected_work"
      title="Follow the build, rescue, and operator notes."
      description="If you are not ready to start a project, join the track that best matches what you are watching. SVM can keep you posted without turning it into a sales-ready intake."
    />
  </PublicLayout>
);

export const AboutPage = () => (
  <PublicLayout>
    <SEO
      title="About - Shock Value Media"
      description="Shock Value Media helps organizations build and run useful apps, workflows, and operating systems with ongoing support."
      url="/about"
    />
    <section className="py-16 md:py-24">
      <div className="container mx-auto grid gap-10 px-4 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
        <div>
          <p className="mb-4 text-sm font-semibold uppercase tracking-normal text-emerald-700">How we work</p>
          <h1 className="mb-6 text-4xl font-bold md:text-6xl">Make the work easier to understand, build, and run.</h1>
        </div>
        <div className="space-y-5 text-lg text-slate-600">
          <p>
            Most organizations do not need another disconnected tool. They need a clearer way to handle the work already in front of them.
          </p>
          <p>
            Shock Value Media starts with the real problem: what comes in, who is involved, where decisions happen, what keeps breaking, and what customers or staff need next.
          </p>
          <p>
            Then we choose and build the right response. That may be a new app, an app rescue, a better workflow, or SVM Operator. We stay involved after launch to observe, secure, maintain, and improve it.
          </p>
        </div>
      </div>
    </section>
    <section className="bg-slate-100 py-16">
      <div className="container mx-auto grid gap-4 px-4 md:grid-cols-3 lg:px-8">
        {[
          [Layers3, "Understand the work before choosing the tool"],
          [Network, "Keep information in the right system"],
          [GitPullRequest, "Stay involved after launch"],
        ].map(([Icon, title]) => {
          const TypedIcon = Icon as typeof Layers3;
          return (
            <div key={title as string} className="rounded-md bg-white p-6">
              <TypedIcon className="mb-4 h-8 w-8 text-slate-950" />
              <h2 className="text-xl font-bold">{title as string}</h2>
            </div>
          );
        })}
      </div>
    </section>
    <UpdatesSignup
      placement="about_bottom"
      title="Keep up with how SVM thinks about useful systems."
      description="Get the practical notes as SVM sharpens public material across app builds, rescues, operators, audits, and venture decisions."
    />
  </PublicLayout>
);

const contactPathOptions = [
  {
    key: "appBuild",
    label: "Build a new app",
    description: "Shape a focused first release around a real business need.",
    icon: Code2,
  },
  {
    key: "appRescue",
    label: "Rescue my app",
    description: "Find what blocks an existing app, repo, or no-code build.",
    icon: GitPullRequest,
  },
  {
    key: "aiOperator",
    label: "Set up SVM Operator",
    description: "Organize requests, decisions, owners, and follow-up.",
    icon: Blocks,
  },
  {
    key: "systemsAudit",
    label: "Audit my systems",
    description: "Find where work is lost, repeated, or delayed.",
    icon: SearchCheck,
  },
  {
    key: "ventureEvaluation",
    label: "Evaluate an opportunity",
    description: "Make a clearer move, pause, partner, sell, or pass decision.",
    icon: Compass,
  },
] as const;

export const ContactPage = () => {
  const [selectedPath, setSelectedPath] =
    useState<(typeof contactPathOptions)[number]["key"]>("appBuild");

  return (
    <PublicLayout>
      <SEO
        title="Contact - Shock Value Media"
        description="Tell Shock Value Media what you need to build, fix, organize, or decide. Plain-language intake and guided project planning are available."
        url="/contact"
      />
      <section className="svm-ink-grid bg-[#080b0f] py-10 text-white md:py-14">
        <div className="container mx-auto grid gap-7 px-4 md:grid-cols-[1fr_auto] md:items-end lg:px-8">
          <div>
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-amber-300">Start a project</p>
            <h1 className="svm-display-tight max-w-4xl text-4xl leading-[0.92] md:text-6xl">Start with the work in front of you.</h1>
            <p className="mt-5 max-w-2xl text-lg font-medium leading-8 text-slate-300">
              Choose the closest path, describe the situation, and leave the technical planning to us.
            </p>
          </div>
          <TrackedLink
            to="/project-planner"
            tracking={{
              label: "Get an AI Capacity Snapshot",
              destination: "/project-planner",
              placement: "contact_hero_secondary",
              service: "project_translator",
            }}
            className="inline-flex md:justify-self-end"
          >
            <Button variant="outline" className="w-full gap-2 rounded-md border-white/30 bg-white/5 font-bold text-white hover:bg-white hover:text-slate-950 sm:w-auto">
              Get an AI Capacity Snapshot
              <ArrowRight className="h-4 w-4" />
            </Button>
          </TrackedLink>
        </div>
      </section>

      <section className="border-b border-slate-300 bg-[#f3eee4] py-8 md:py-10">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="mb-5 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-rose-700">Choose a starting point</p>
              <h2 className="svm-display-tight text-3xl md:text-4xl">What is closest to the work?</h2>
            </div>
            <p className="text-sm font-medium text-slate-600">You can change this before sending.</p>
          </div>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-5">
            {contactPathOptions.map((option) => {
              const isActive = option.key === selectedPath;
              const Icon = option.icon;
              return (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => {
                    setSelectedPath(option.key);
                    trackPublicFunnelEvent("service_path_selected", {
                      placement: "contact_path_selector",
                      service: entryPointConfigs[option.key].opportunityType,
                      entry_point: entryPointConfigs[option.key].entryPoint,
                    });
                  }}
                  aria-pressed={isActive}
                  className={`min-h-32 rounded-md border p-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-600 focus-visible:ring-offset-2 sm:p-4 ${
                    isActive
                      ? "border-slate-950 bg-slate-950 text-white"
                      : "border-slate-300 bg-white text-slate-950 hover:border-slate-500 hover:shadow-md"
                  }`}
                >
                  <Icon className={`mb-5 h-5 w-5 ${isActive ? "text-amber-300" : "text-rose-700"}`} />
                  <span className="block text-sm font-bold leading-5">{option.label}</span>
                  <span className={`mt-2 block text-xs font-medium leading-5 ${isActive ? "text-slate-300" : "text-slate-600"}`}>
                    {option.description}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <OpportunityIntakeForm key={selectedPath} config={entryPointConfigs[selectedPath]} />
      <UpdatesSignup
        placement="contact_after_intake"
        title="Not ready to send a brief?"
        description="Use the lighter updates lane. Pick what you want to hear about and SVM will route it as nurture instead of an active project request."
      />
    </PublicLayout>
  );
};

const LegalShell = ({
  eyebrow,
  title,
  updated,
  children,
}: {
  eyebrow: string;
  title: string;
  updated: string;
  children: ReactNode;
}) => (
  <PublicLayout>
    <section className="py-16 md:py-24">
      <div className="container mx-auto max-w-3xl px-4 lg:px-8">
        <p className="mb-4 text-sm font-semibold uppercase tracking-normal text-emerald-700">{eyebrow}</p>
        <h1 className="mb-3 text-4xl font-bold md:text-5xl">{title}</h1>
        <p className="mb-10 text-sm font-medium text-slate-500">Last updated {updated}</p>
        <div className="space-y-8 text-slate-700 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-slate-950 [&_p]:mt-3 [&_p]:leading-relaxed [&_ul]:mt-3 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5">
          {children}
        </div>
      </div>
    </section>
  </PublicLayout>
);

export const PrivacyPage = () => (
  <LegalShell eyebrow="Privacy" title="Privacy Policy" updated="July 14, 2026">
    <SEO
      title="Privacy Policy - Shock Value Media"
      description="How Shock Value Media collects, uses, and protects the business context you share through the site."
      url="/privacy"
    />
    <section>
      <h2>What this covers</h2>
      <p>
        This policy explains how Shock Value Media ("SVM", "we") handles the information you choose to
        share through shockvaluemedia.com, including the Project Translator and opportunity intake forms. It applies to this
        public site only.
      </p>
    </section>
    <section>
      <h2>What we collect</h2>
      <p>When you submit an intake form, we receive the business context you enter, which may include:</p>
      <ul>
        <li>Your name, email, and (optionally) phone number.</li>
        <li>Company or project name, website, role, stage, timeline, budget range, and urgency.</li>
        <li>A description of the app, workflow, system, or opportunity you want reviewed.</li>
        <li>Your Project Translator answers and the draft plan prepared from them.</li>
        <li>Standard context such as the page you submitted from and any campaign (UTM) parameters in the link you followed.</li>
      </ul>
      <p>
        Please share business context only. Do not submit credentials, protected client details, case
        files, health information, student records, or other sensitive source-system data.
      </p>
    </section>
    <section>
      <h2>How we use it</h2>
      <p>
        We use the information to review your request, decide whether it is a fit, and follow up with a
        recommended next step. Submissions are routed into SVM's private operations workspace ("Operator")
        for that review. We do not sell your information or share it with third parties for their own
        marketing.
      </p>
    </section>
    <section>
      <h2>AI-assisted project drafts</h2>
      <p>
        When you ask the Project Translator to prepare a draft, your answers may be sent to SVM's AI service.
        The service uses them to summarize the problem and suggest a starting path. It may keep operational logs
        for security, reliability, and troubleshooting. Do not include passwords, credentials, private customer
        records, regulated information, or anything you are not allowed to disclose.
      </p>
      <p>
        The draft is not a final scope, quote, architecture, timeline, or automated decision. A person at SVM
        reviews the context before recommending work. If you send the draft through the intake form, it becomes
        part of the private request SVM reviews and follows up on.
      </p>
    </section>
    <section>
      <h2>Consent and follow-up</h2>
      <p>
        We only submit your intake after you check the consent box on the form. If we follow up by email,
        every message includes a way to unsubscribe, and we honor opt-out requests. You can ask us to
        access, correct, or delete your information at any time.
      </p>
    </section>
    <section>
      <h2>Retention and security</h2>
      <p>
        We keep intake records for as long as needed to evaluate and act on the opportunity, then for a
        reasonable period for our records. Access is limited to SVM and the tools it uses to operate.
      </p>
    </section>
    <section>
      <h2>Contact</h2>
      <p>
        Questions or requests: <a className="font-bold underline" href="mailto:info@shockvaluemedia.com">info@shockvaluemedia.com</a>.
      </p>
    </section>
  </LegalShell>
);

export const TermsPage = () => (
  <LegalShell eyebrow="Terms" title="Terms of Use" updated="July 14, 2026">
    <SEO
      title="Terms of Use - Shock Value Media"
      description="The terms that govern use of the Shock Value Media public site."
      url="/terms"
    />
    <section>
      <h2>Using this site</h2>
      <p>
        shockvaluemedia.com is an informational site that lets you share a business opportunity with Shock
        Value Media. By using it you agree to use it lawfully and to provide accurate information.
      </p>
    </section>
    <section>
      <h2>No guarantee of engagement</h2>
      <p>
        Submitting an intake form starts a review; it does not create a contract or guarantee that SVM will
        take on the work. Any engagement is governed by a separate written agreement.
      </p>
    </section>
    <section>
      <h2>Project Translator drafts</h2>
      <p>
        Project Translator output is an early planning aid. It may be incomplete or wrong and is not a quote,
        final scope, technical architecture, delivery timeline, or professional advice. SVM reviews the context
        with you before recommending or agreeing to work.
      </p>
    </section>
    <section>
      <h2>Your submissions</h2>
      <p>
        You are responsible for what you submit and confirm you have the right to share it. Do not submit
        confidential third-party data or anything you are not permitted to disclose.
      </p>
    </section>
    <section>
      <h2>As-is</h2>
      <p>
        The site is provided "as is" without warranties. To the extent permitted by law, SVM is not liable
        for damages arising from use of the site.
      </p>
    </section>
    <section>
      <h2>Contact</h2>
      <p>
        Questions: <a className="font-bold underline" href="mailto:info@shockvaluemedia.com">info@shockvaluemedia.com</a>.
      </p>
    </section>
  </LegalShell>
);

export const NotFoundPublic = () => (
  <PublicLayout>
    <section className="py-24">
      <div className="container mx-auto px-4 text-center lg:px-8">
        <BriefcaseBusiness className="mx-auto mb-6 h-12 w-12 text-slate-400" />
        <h1 className="mb-4 text-4xl font-bold">We could not find that page.</h1>
        <p className="mx-auto mb-8 max-w-2xl text-slate-600">
          Start again from the homepage, or use the Project Translator if you are trying to figure out what kind of help you need.
        </p>
        <div className="flex flex-col justify-center gap-3 sm:flex-row">
          <TrackedLink
            to="/"
            tracking={{
              label: "Return Home",
              destination: "/",
              placement: "not_found_primary",
            }}
          >
            <Button>Return Home</Button>
          </TrackedLink>
          <TrackedLink
            to="/project-planner"
            tracking={{
              label: "Help Me Choose",
              destination: "/project-planner",
              placement: "not_found_secondary",
              service: "project_translator",
            }}
          >
            <Button variant="outline">Help Me Choose</Button>
          </TrackedLink>
        </div>
      </div>
    </section>
  </PublicLayout>
);
